/**
 * Performance monitoring utilities for tracking before/after performance improvements
 */

interface PerformanceMetric {
  name: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  metadata?: Record<string, any>;
}

interface MemorySnapshot {
  timestamp: number;
  usedJSMemory: number;
  totalJSMemory: number;
  jsMemoryLimit: number;
}

interface RenderMetrics {
  componentName: string;
  renderCount: number;
  lastRenderTime: number;
  totalRenderTime: number;
  avgRenderTime: number;
}

class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: Map<string, PerformanceMetric> = new Map();
  private memorySnapshots: MemorySnapshot[] = [];
  private renderMetrics: Map<string, RenderMetrics> = new Map();
  private isProduction = process.env.NODE_ENV === 'production';

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  /**
   * Start timing a performance metric
   */
  startTiming(name: string, metadata?: Record<string, any>): void {
    if (this.isProduction) return;

    this.metrics.set(name, {
      name,
      startTime: performance.now(),
      metadata,
    });
  }

  /**
   * End timing and calculate duration
   */
  endTiming(name: string): number | null {
    if (this.isProduction) return null;

    const metric = this.metrics.get(name);
    if (!metric) {
      console.warn(`Performance metric '${name}' was not started`);
      return null;
    }

    const endTime = performance.now();
    const duration = endTime - metric.startTime;

    this.metrics.set(name, {
      ...metric,
      endTime,
      duration,
    });


    return duration;
  }

  /**
   * Measure Redux action performance
   */
  measureReduxAction<T>(actionName: string, action: () => T): T {
    if (this.isProduction) return action();

    this.startTiming(`redux-${actionName}`);
    const result = action();
    this.endTiming(`redux-${actionName}`);
    return result;
  }

  /**
   * Measure async Redux thunk performance
   */
  async measureReduxThunk<T>(thunkName: string, thunk: () => Promise<T>): Promise<T> {
    if (this.isProduction) return await thunk();

    this.startTiming(`redux-thunk-${thunkName}`);
    try {
      const result = await thunk();
      this.endTiming(`redux-thunk-${thunkName}`);
      return result;
    } catch (error) {
      this.endTiming(`redux-thunk-${thunkName}`);
      throw error;
    }
  }

  /**
   * Take memory snapshot
   */
  takeMemorySnapshot(): MemorySnapshot | null {
    if (this.isProduction || !('memory' in performance)) return null;

    const memory = (performance as any).memory;
    const snapshot: MemorySnapshot = {
      timestamp: Date.now(),
      usedJSMemory: memory.usedJSMemory,
      totalJSMemory: memory.totalJSMemory,
      jsMemoryLimit: memory.jsMemoryLimit,
    };

    this.memorySnapshots.push(snapshot);

    // Keep only last 100 snapshots
    if (this.memorySnapshots.length > 100) {
      this.memorySnapshots.shift();
    }

    return snapshot;
  }

  /**
   * Track component render performance
   */
  trackRender(componentName: string, renderTime: number): void {
    if (this.isProduction) return;

    const existing = this.renderMetrics.get(componentName);
    if (existing) {
      const newCount = existing.renderCount + 1;
      const newTotal = existing.totalRenderTime + renderTime;

      this.renderMetrics.set(componentName, {
        componentName,
        renderCount: newCount,
        lastRenderTime: renderTime,
        totalRenderTime: newTotal,
        avgRenderTime: newTotal / newCount,
      });
    } else {
      this.renderMetrics.set(componentName, {
        componentName,
        renderCount: 1,
        lastRenderTime: renderTime,
        totalRenderTime: renderTime,
        avgRenderTime: renderTime,
      });
    }
  }

  /**
   * Get performance summary
   */
  getPerformanceSummary(): {
    metrics: PerformanceMetric[];
    memoryUsage: MemorySnapshot[];
    renderMetrics: RenderMetrics[];
    slowestOperations: PerformanceMetric[];
    memoryLeaks: boolean;
  } {
    const metrics = Array.from(this.metrics.values()).filter(m => m.duration);
    const renderMetrics = Array.from(this.renderMetrics.values());

    // Find slowest operations (> 16ms for 60fps)
    const slowestOperations = metrics
      .filter(m => m.duration && m.duration > 16)
      .sort((a, b) => (b.duration || 0) - (a.duration || 0));

    // Basic memory leak detection
    const memoryLeaks = this.detectMemoryLeaks();

    return {
      metrics,
      memoryUsage: this.memorySnapshots,
      renderMetrics,
      slowestOperations,
      memoryLeaks,
    };
  }

  /**
   * Simple memory leak detection
   */
  private detectMemoryLeaks(): boolean {
    if (this.memorySnapshots.length < 10) return false;

    const recent = this.memorySnapshots.slice(-10);
    const first = recent[0];
    const last = recent[recent.length - 1];

    // If memory usage increased by more than 10MB over 10 samples
    const memoryIncrease = last.usedJSMemory - first.usedJSMemory;
    return memoryIncrease > 10 * 1024 * 1024; // 10MB
  }

  /**
   * Export performance data for analysis
   */
  exportData(): string {
    const summary = this.getPerformanceSummary();
    return JSON.stringify(summary, null, 2);
  }

  /**
   * Clear all performance data
   */
  clear(): void {
    this.metrics.clear();
    this.memorySnapshots = [];
    this.renderMetrics.clear();
  }

  /**
   * Log performance warnings
   */
  logWarnings(): void {
    if (this.isProduction) return;

    const summary = this.getPerformanceSummary();

    if (summary.slowestOperations.length > 0) {
      console.warn('🐌 Slow operations detected:', summary.slowestOperations);
    }

    if (summary.memoryLeaks) {
      console.warn('🚨 Potential memory leak detected!');
    }

    // Check for components with excessive renders
    const excessiveRenders = summary.renderMetrics.filter(m => m.renderCount > 10);
    if (excessiveRenders.length > 0) {
      console.warn('🔄 Components with excessive renders:', excessiveRenders);
    }
  }
}

export const performanceMonitor = PerformanceMonitor.getInstance();

// Helper hook for component render tracking
export const useRenderTracker = (componentName: string) => {
  if (process.env.NODE_ENV === 'production') return;

  const startTime = performance.now();

  return () => {
    const renderTime = performance.now() - startTime;
    performanceMonitor.trackRender(componentName, renderTime);
  };
};

// Helper decorator for Redux actions
export const measureReduxAction = (actionName: string) => {
  return function (_target: any, propertyName: string, descriptor: PropertyDescriptor) {
    const method = descriptor.value;

    descriptor.value = function (...args: any[]) {
      return performanceMonitor.measureReduxAction(
        `${actionName}-${propertyName}`,
        () => method.apply(this, args)
      );
    };
  };
};